import type { Nullish } from '@payaca/utilities/types';
import { z } from 'zod/v4';
import type { ProposalSummaryMessage } from './jobTypesV2';
import type { StoredPaymentSchedule } from './payment-schedule';
import type { Tag } from './tagTypes';
import type { Upload } from './uploadTypes';

export type TemplatableEntity = 'task' | 'scheduledEvent' | 'proposal';

export type EntityTemplateSource = 'service_plan_commitment';

export type EntityTemplate<TEntityType extends TemplatableEntity> = {
  id: number;
  publicId: string;
  name?: Nullish<string>;
  description?: Nullish<string>;
  entityType: TEntityType;
  data: Partial<EntityTemplateData[TEntityType]>;
  accountId?: number;
  metaData?: {
    source?: EntityTemplateSource;
  };
};

export type HydratedEntityTemplate<TEntityType extends TemplatableEntity> =
  EntityTemplate<TEntityType> & {
    servicePlans: {
      publicId: string;
      name: string;
    }[];
    uploads: Upload[];
  };

export type PublicEntityTemplate<TEntityType extends TemplatableEntity> = Omit<
  EntityTemplate<TEntityType>,
  'id'
>;

export type PublicHydratedEntityTemplate<
  TEntityType extends TemplatableEntity,
> = Omit<HydratedEntityTemplate<TEntityType>, 'id'>;

/**
 * Assignee carried by a task template, in the shape crossing the API to the
 * client. `user` references a specific user by public id; `team` references a
 * Team by public id; `projectOwner` resolves against the project a task is
 * created on, at creation time; `userCustomField` resolves to the user held in
 * a user-type custom field on that project, addressed by identifier — a
 * top-level field's identifier, or a dot path into a fieldset, e.g.
 * `projectManager` or `projectAdmin.projectManager`.
 * (`user` and `team` are stored internally with numeric ids; coerced at the
 * API boundary. The other variants are stored as-is.)
 *
 * The type is inferred from the schema so a variant cannot be added to one
 * without the other: the boundary parses against this, and a variant the schema
 * does not know is dropped rather than stored.
 */
export const taskTemplateAssigneeWireSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), userPublicId: z.string() }),
  z.object({ type: z.literal('team'), teamPublicId: z.string() }),
  z.object({ type: z.literal('projectOwner') }),
  z.object({
    type: z.literal('userCustomField'),
    customFieldIdentifier: z.string(),
  }),
]);

export type TaskTemplateAssignee = z.infer<
  typeof taskTemplateAssigneeWireSchema
>;

type EntityTemplateData = {
  task: {
    name: string;
    notes: string;
    checklistItems: Array<string>;
    formTemplateId?: Nullish<number>;
    customFieldIdentifiers?: Nullish<Array<string>>;
    scheduledEvents?: Nullish<Array<ScheduledEventTemplate>>;
    assignee?: Nullish<TaskTemplateAssignee>;
    deadlineOffsetDays?: Nullish<number>;
  };
  scheduledEvent: {
    name: string;
    description: string;
    duration?: Nullish<string>; // ISO 8601 duration string
    tasks?: Nullish<Array<Omit<PublicEntityTemplate<'task'>, 'publicId'>>>;
    uploadIds?: Nullish<Array<Upload['id']>>;
    tagIds?: Nullish<Array<Tag['id']>>;
  };
  proposal: {
    type?: 'ESTIMATE' | 'QUOTE' | null;
    introduction?: string | object | object[] | null;
    notes?: string | object | object[] | null;
    validForDays?: Nullish<number>;
    paymentSchedule?: Nullish<StoredPaymentSchedule>;
    summaryMessages?: Nullish<ProposalSummaryMessage[]>;
    grantIds?: Nullish<string[]>;
  };
};

export type TaskTemplate = EntityTemplate<'task'>;

export type ScheduledEventTemplate = EntityTemplate<'scheduledEvent'>;

export enum EntityTemplatePlaceholderVariables {
  CUSTOMER_NAME = 'customer.name',
  CUSTOMER_PRIMARYCONTACT_NAME = 'customer.primaryContact.name',
  CUSTOMER_PRIMARYCONTACT_FIRSTNAME = 'customer.primaryContact.firstName',
  PROJECT_SITEADDRESSES_ARRAYSTRING = 'deal.siteAddresses.arrayString',
  PROJECT_CUSTOMFIELD_VALUE = 'deal.customField.value',
  ACCOUNT_CUSTOMFIELD_VALUE = 'account.customField.value',
}

export type ProposalTemplatePlaceholderVariables =
  | EntityTemplatePlaceholderVariables.CUSTOMER_NAME
  | EntityTemplatePlaceholderVariables.PROJECT_SITEADDRESSES_ARRAYSTRING;
